import { useContext, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { checkCashoutTicket } from "@app/dev/actions/ui";
import { TicketsContext } from "@tickets/store/context";
import { getTicketType } from "@tickets/helpers/type";
import { usePrevious } from "@common/hooks/previous";
import { getTicketData } from "@tickets/services";
import Config from "@app/dev/config/main";
import { BetStateEnum } from "@app/dev/constants/enums/betState";

const getTicketBarcode = (ticket) => ticket.barcode || ticket.dataFromSwarm?.barcode;

const pollingBarcodes = new Set();

export const useTicketsSync = (activeRowIndex, setCashoutAmount, setLoading) => {
  const dispatch = useDispatch();
  const [tickets, setTickets] = useContext(TicketsContext);

  const cashoutTicketState = useSelector((state) => state.uiState.cashoutTicket?.Data);

  const cashoutTicketBarcode = typeof cashoutTicketState === "object" ? cashoutTicketState.Id : cashoutTicketState;
  const prevCashoutTicketBarcode = usePrevious(cashoutTicketBarcode);

  useEffect(() => {
    if (cashoutTicketBarcode !== prevCashoutTicketBarcode) setLoading(false);
  }, [prevCashoutTicketBarcode, cashoutTicketBarcode, activeRowIndex, setLoading]);

  useEffect(() => {
    return () => dispatch(checkCashoutTicket(null));
  }, []);

  const ticketData = activeRowIndex !== null && tickets.sport[activeRowIndex];

  useEffect(() => {
    const ticket = ticketData?.dataFromSwarm;
    if (ticket) {
      (async () => {
        const data = ticketData;

        setCashoutAmount(data?.dataFromSwarm.cashout_amount);

        if (prevCashoutTicketBarcode !== cashoutTicketBarcode) {
          const data = await getTicketData(ticket.barcode);

          setCashoutAmount(data?.dataFromSwarm.cashout_amount);

          const type = getTicketType(data?.dataFromSwarm.barcode?.toString());

          setTickets((prevArray) => {
            const newArray = [...prevArray[type]];
            newArray[activeRowIndex] = {
              ...newArray[activeRowIndex],
              ...data,
              paid: !!data.dataFromSwarm?.paid_date,
            };

            return {
              ...prevArray,
              [type]: newArray,
            };
          });
        }
      })();

      const needToPollTicket =
        Config.main.enableTicketAutoCashoutAmountUpdate &&
        ticket.is_live &&
        ticket.state_id === BetStateEnum.Accepted &&
        !pollingBarcodes.has(ticket.barcode); // temp fix

      if (needToPollTicket) {
        pollingBarcodes.add(ticket.barcode);

        const interval = setInterval(async () => {
          const ticketBarcode = ticket.barcode;

          const result = await getTicketData(ticketBarcode);
          const type = getTicketType(result?.dataFromSwarm.barcode?.toString());

          setCashoutAmount(result?.dataFromSwarm.cashout_amount);

          if (ticket.state_name !== result?.dataFromSwarm.state_name) {
            if (!Config.main.enableTicketTimer) {
              setTickets((prevArray) => {
                const newArray = [...prevArray.sport];
                newArray[activeRowIndex] = {
                  ...newArray[activeRowIndex],
                  ...result,
                };

                return {
                  ...prevArray,
                  [type]: newArray,
                };
              });
            } else {
              const ticketIndex = tickets[type].findIndex(
                (item) => getTicketBarcode(item) === getTicketBarcode(result)
              );
              const updatedTickets = [...tickets[type]];
              updatedTickets[ticketIndex] = { ...result };

              setTickets({
                ...tickets,
                [type]: updatedTickets,
              });
            }
          }
        }, 10 * 1000);

        return () => {
          pollingBarcodes.delete(ticket.barcode);
          clearInterval(interval);
        };
      }
    }
  }, [activeRowIndex, ticketData, prevCashoutTicketBarcode, cashoutTicketBarcode]);
};

export default useTicketsSync;
